import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getSupabase } from '../lib/supabase';
import { authErrorMessage } from '../lib/authErrors';
import { useAuth } from '../providers/AuthProvider';
import { LoadingScreen } from '../components/feedback/LoadingScreen';
import { Button, EyeIcon, EyeOffIcon, IconButton, Input, Label } from '../ui';
import logoUrl from '../assets/trade-georgia-logo.webp';

// Authentication entry. Split-screen sign-in: a brand panel on the left, the credential form
// on the right. Presentation only — the email/password flow, the Georgian error mapping and
// the already-authenticated redirect are unchanged.
//
// The whole screen opts into the dark palette via `data-theme="dark"` (styles/tokens.css
// re-declares the same token variables for any such subtree). The login screen carries no
// theme toggle and the stored theme defaults to light, so without this the branded panel
// would render on a light background it was not designed for. No color is hardcoded here —
// every surface below is one of the existing tokens (bg / surface / fg / fg-muted / primary).
interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage(): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'loading') {
    return <LoadingScreen />;
  }
  if (status === 'authenticated') {
    const to = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: signInError } = await getSupabase().auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) {
      // Supabase returns English here ("Invalid login credentials"), which used to reach the
      // screen verbatim — the only English string left in a Georgian-only interface.
      setError(authErrorMessage(signInError, 'შესვლა ვერ მოხერხდა. სცადეთ ხელახლა.'));
    }
    setSubmitting(false);
  };

  return (
    <div data-theme="dark" className="grid min-h-full w-full grid-cols-1 bg-bg lg:grid-cols-2">
      {/* Brand panel — desktop only; the form panel carries the logo on narrow screens. */}
      <section className="hidden flex-col bg-bg px-16 py-12 lg:flex">
        <div className="flex flex-1 flex-col justify-center">
          <img
            src={logoUrl}
            alt="Trade Georgia"
            className="h-auto w-[17.5rem] max-w-full"
            width={2000}
            height={246}
          />
          <p className="mt-14 max-w-[16ch] text-4xl font-semibold leading-tight text-fg">
            დებიტორების მართვა
          </p>
        </div>
        <p className="text-sm text-fg-muted">© {new Date().getFullYear()} Trade Georgia</p>
      </section>

      {/* Form panel — one tonal step up from the brand panel (surface over bg). */}
      <section className="flex items-center justify-center bg-surface px-6 py-12 sm:px-10">
        <div className="w-full max-w-md">
          <img
            src={logoUrl}
            alt="Trade Georgia"
            className="mb-10 h-auto w-[14rem] max-w-full lg:hidden"
            width={2000}
            height={246}
          />
          <h1 className="text-3xl font-semibold text-fg">სისტემაში შესვლა</h1>
          <p className="mt-3 text-sm leading-relaxed text-fg-muted">
            შეიყვანეთ თქვენი მონაცემები სამუშაო სივრცეში შესასვლელად
          </p>

          <form onSubmit={(e) => void onSubmit(e)} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="login-email">ელფოსტა</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                placeholder="ელფოსტა"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="login-password">პაროლი</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={passwordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="პაროლი"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pe-12"
                />
                <IconButton
                  type="button"
                  label={passwordVisible ? 'პაროლის დამალვა' : 'პაროლის ჩვენება'}
                  icon={
                    passwordVisible ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )
                  }
                  onClick={() => setPasswordVisible((prev) => !prev)}
                  className="absolute end-1 top-0 text-fg-muted hover:text-fg"
                />
              </div>
            </div>

            {error !== null && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}

            <Button type="submit" size="lg" loading={submitting} className="w-full">
              შესვლა
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
